import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, MapPin, Trophy, UserPlus, X, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Showcase = () => {
  const [upcomingShowcases, setUpcomingShowcases] = useState([]);
  const [standouts, setStandouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Registration modal state
  const [showModal, setShowModal] = useState(false);
  const [selectedShowcase, setSelectedShowcase] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [formData, setFormData] = useState({
    player_name: '',
    email: '',
    phone: '',
    graduation_year: '',
    high_school: '',
    positions: '',
    parent_name: '',
    notes: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [showcasesRes, standoutsRes] = await Promise.all([
          axios.get(`${API}/showcases?type=upcoming`),
          axios.get(`${API}/standouts`)
        ]);
        setUpcomingShowcases(showcasesRes.data);
        setStandouts(standoutsRes.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching showcase data:', err);
        setError('Failed to load showcase data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  // Group standouts by event
  const standoutsByEvent = standouts.reduce((acc, standout) => {
    if (!acc[standout.event_name]) {
      acc[standout.event_name] = {
        event_name: standout.event_name,
        event_date: standout.event_date,
        players: []
      };
    }
    acc[standout.event_name].players.push(standout);
    return acc;
  }, {});

  const standoutEvents = Object.values(standoutsByEvent);

  const handleRegisterClick = (showcase) => {
    setSelectedShowcase(showcase);
    setFormData({
      player_name: '',
      email: '',
      phone: '',
      graduation_year: '',
      high_school: '',
      positions: '',
      parent_name: '',
      notes: ''
    });
    setSubmitSuccess(false);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedShowcase(null);
    setSubmitSuccess(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitRegistration = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await axios.post(`${API}/showcase-registrations`, {
        showcase_id: selectedShowcase.id,
        showcase_name: selectedShowcase.name,
        ...formData
      });
      setSubmitSuccess(true);
    } catch (err) {
      console.error('Error registering:', err);
      alert('Failed to register. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="page-content">
        <section className="page-hero">
          <div className="container">
            <h1 className="display-large">Showcases</h1>
            <p className="body-large">Loading...</p>
          </div>
        </section>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-content">
        <section className="page-hero">
          <div className="container">
            <h1 className="display-large">Showcases</h1>
            <p className="body-large error-message">{error}</p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="page-content">
      <section className="page-hero">
        <div className="container">
          <h1 className="display-large">Showcases</h1>
          <p className="body-large">Elite tournaments and player development events</p>
        </div>
      </section>

      {/* Upcoming Showcases */}
      <section className="showcases-section">
        <div className="container">
          <h2 className="heading-1">Upcoming Showcases</h2>
          {upcomingShowcases.length === 0 ? (
            <p className="body-large" style={{ textAlign: 'center' }}>No upcoming showcases at this time.</p>
          ) : (
            <div className="showcases-grid">
              {upcomingShowcases.map((showcase) => (
                <div key={showcase.id} className="showcase-card network-card">
                  <h3 className="network-card-title">{showcase.name}</h3>
                  <div className="showcase-details">
                    <div className="detail-row">
                      <Calendar size={20} />
                      <span>{new Date(showcase.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                    </div>
                    <div className="detail-row">
                      <MapPin size={20} />
                      <span>{showcase.location}</span>
                    </div>
                  </div>
                  <p className="network-card-content">{showcase.description}</p>
                  <div className="showcase-ages">
                    <strong>Age Groups:</strong>
                    <div className="age-tags">
                      {showcase.age_groups.map((age) => (
                        <span key={age} className="age-tag">{age.toUpperCase()}</span>
                      ))}
                    </div>
                  </div>
                  <button 
                    data-testid={`register-btn-${showcase.id}`}
                    onClick={() => handleRegisterClick(showcase)}
                    className="btn-primary"
                    style={{ 
                      marginTop: '16px', 
                      width: '100%', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      gap: '8px' 
                    }}
                  >
                    <UserPlus size={20} /> Register Now
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Standouts Section */}
      {standoutEvents.length > 0 && (
        <section className="standouts-section">
          <div className="container">
            <h2 className="heading-1">Event Standouts</h2>
            {standoutEvents.map((event) => (
              <div key={event.event_name} className="standout-event">
                <div className="event-header">
                  <h3 className="heading-2">{event.event_name}</h3>
                  <p className="body-medium">{new Date(event.event_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
                </div>
                <div className="standouts-grid">
                  {event.players.map((player) => (
                    <div key={player.id} className="standout-card network-card">
                      <div className="standout-photo">
                        <img src={player.photo_url} alt={player.player_name} />
                        <div className="trophy-badge">
                          <Trophy size={24} />
                        </div>
                      </div>
                      <div className="standout-info">
                        <h4 className="network-card-title">{player.player_name}</h4>
                        <div className="standout-metric">
                          <span className="metric-label">{player.metric}</span>
                          <span className="metric-value">{player.value}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Registration Modal */}
      {showModal && selectedShowcase && (
        <div 
          data-testid="registration-modal"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
          onClick={(e) => e.target === e.currentTarget && handleCloseModal()}
        >
          <div style={{
            background: 'var(--bg-secondary)',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '500px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            position: 'relative',
            border: '1px solid var(--border-medium)'
          }}>
            <button 
              onClick={handleCloseModal}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--text-light)'
              }}
            >
              <X size={24} />
            </button>

            {submitSuccess ? (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  background: 'rgba(34, 197, 94, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px'
                }}>
                  <Check size={40} style={{ color: '#22c55e' }} />
                </div>
                <h2 className="heading-2" style={{ marginBottom: '16px' }}>Registration Successful!</h2>
                <p className="body-large" style={{ marginBottom: '8px' }}>
                  You've been registered for <strong>{selectedShowcase.name}</strong>
                </p>
                <p className="body-medium" style={{ color: 'var(--text-light)', marginBottom: '24px' }}>
                  A confirmation email has been sent to your email address.
                </p>
                <button onClick={handleCloseModal} className="btn-primary">Close</button>
              </div>
            ) : (
              <>
                <h2 className="heading-2" style={{ marginBottom: '8px' }}>Register for Showcase</h2>
                <p className="body-medium" style={{ color: 'var(--text-light)', marginBottom: '24px' }}>
                  {selectedShowcase.name} - {new Date(selectedShowcase.date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </p>

                <form onSubmit={handleSubmitRegistration}>
                  <div className="form-group" style={{ marginBottom: '16px' }}>
                    <label>Player Name *</label>
                    <input
                      type="text"
                      name="player_name"
                      value={formData.player_name}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter player's full name"
                    />
                  </div>

                  <div className="form-group" style={{ marginBottom: '16px' }}>
                    <label>Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter email address"
                    />
                  </div>

                  <div className="form-group" style={{ marginBottom: '16px' }}>
                    <label>Phone *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter phone number"
                    />
                  </div>

                  <div className="form-group" style={{ marginBottom: '16px' }}>
                    <label>Graduation Year *</label>
                    <input
                      type="text"
                      name="graduation_year"
                      value={formData.graduation_year}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g., 2028"
                      maxLength="4"
                    />
                  </div>

                  <div className="form-group" style={{ marginBottom: '16px' }}>
                    <label>High School *</label>
                    <input
                      type="text"
                      name="high_school"
                      value={formData.high_school}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter high school name"
                    />
                  </div>

                  <div className="form-group" style={{ marginBottom: '16px' }}>
                    <label>Position(s) *</label>
                    <input
                      type="text"
                      name="positions"
                      value={formData.positions}
                      onChange={handleInputChange}
                      required
                      placeholder="e.g., RHP/SS, OF/1B"
                    />
                  </div>

                  <div className="form-group" style={{ marginBottom: '16px' }}>
                    <label>Parent/Guardian Name</label>
                    <input
                      type="text"
                      name="parent_name"
                      value={formData.parent_name}
                      onChange={handleInputChange}
                      placeholder="Enter parent/guardian name (if applicable)"
                    />
                  </div>

                  <div className="form-group" style={{ marginBottom: '24px' }}>
                    <label>Additional Notes</label>
                    <textarea
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      rows="3"
                      placeholder="Any additional information..."
                    />
                  </div>

                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button 
                      type="submit" 
                      className="btn-primary" 
                      disabled={submitting}
                      style={{ flex: 1 }}
                    >
                      {submitting ? 'Submitting...' : 'Complete Registration'}
                    </button>
                    <button 
                      type="button" 
                      onClick={handleCloseModal} 
                      className="btn-secondary"
                      disabled={submitting}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Showcase;
